import { IEventrHotel } from "@eventr/common";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { useMemo } from "react";
import { routes } from "routing";
import { getCategoryProperties } from "utils";
import { useScreenTypeContext } from "../state/screenTypeProvider";
import { useMatchTagsAndUsers } from "./hooks/useMatchTagsAndUsers";
import { HotelAddress } from "./hotelComponents/hotelAddress";
import { HotelBookingInfo } from "./hotelComponents/hotelBookingInfo";
import { HotelHeader } from "./hotelComponents/hotelHeader";
import { ObjectProvider } from "./providers/objectProvider";
import { BaseCard } from "./subComponents/baseCard";

interface HotelCardProps {
  id?: string;
  data?: IEventrHotel;
  past?: boolean;
  border?: boolean;
  date?: string | undefined;
}

export default function HotelCard({ id, data, past = false, date }: HotelCardProps) {
  const { screenType } = useScreenTypeContext();
  const { pushPath } = useGlobalRouter();
  const { matchTagsAndUser } = useMatchTagsAndUsers();

  const room = useMemo(() => {
    if ((data?.rooms?.length ?? 0) === 0) {
      return undefined;
    }
    const bookedRoom = data?.identifier ? data.rooms?.find((room) => room._id === data.identifier) : undefined;
    return (
      bookedRoom ??
      data?.rooms?.find((room) => {
        return room?.guests?.some((user) => {
          return matchTagsAndUser(user);
        });
      })
    );
  }, [data?.rooms, data?.identifier, matchTagsAndUser]);

  if (!data) return null;

  const attachments = {
    notes: data.notes,
    extra_docs: data.extra_docs,
    website: data.website,
    phoneNumber: data.telephoneNumber,
    contactName: data.contactName,
  };

  const handleClick = () => {
    if (data._id) {
      pushPath(routes.item.hotel.build({ itemId: data._id }), { state: { screenType, date, roomId: data.identifier } });
    }
  };

  return (
    <ObjectProvider past={past} _id={data._id ?? ""} sectionID="3">
      {screenType === "upNext" ? (
        <BaseCard id={id} past={past} onClick={handleClick}>
          <HotelHeader
            category={getCategoryProperties(data?.categories?.[0])}
            name={data.name}
            room={room}
            label={data.label ?? ""}
            labelTime={data.labelTime}
            visibility={data.visibility}
          />
          {data.OSMAddress && <HotelAddress address={data.OSMAddress} />}
          <HotelBookingInfo
            attachments={attachments}
            rooms={data.rooms}
            room={room}
            chatNames={[data.name]}
            providerData={data.hotelProvider}
          />
        </BaseCard>
      ) : screenType === "edit" ? (
        <BaseCard id={id} past={past}>
          <HotelHeader
            category={getCategoryProperties(data?.categories?.[0])}
            name={data.name}
            room={room}
            label={data.label ?? ""}
            visibility={data.visibility}
          />
          {data.OSMAddress && <HotelAddress address={data.OSMAddress} />}
          <HotelBookingInfo attachments={attachments} rooms={data.rooms} room={room} chatNames={[data.name]} />
        </BaseCard>
      ) : screenType === "personal" ? (
        <BaseCard id={id} past={past} onClick={handleClick}>
          <HotelHeader
            category={getCategoryProperties(data?.categories?.[0])}
            name={data.name}
            room={room}
            label={data.label ?? ""}
            visibility={data.visibility}
          />
          {data.OSMAddress && <HotelAddress address={data.OSMAddress} />}
          <HotelBookingInfo attachments={attachments} rooms={data.rooms} room={room} chatNames={[data.name]} />
        </BaseCard>
      ) : screenType === "team" || screenType === "teamSearch" || screenType === "teamMember" ? (
        <BaseCard id={id} past={past} onClick={handleClick}>
          <HotelHeader
            category={getCategoryProperties(data?.categories?.[0])}
            name={data.name}
            room={room}
            label={data.label ?? ""}
            visibility={data.visibility}
            date={date}
            rooms={data?.rooms}
          />
          {data.OSMAddress && <HotelAddress address={data.OSMAddress} />}
          <HotelBookingInfo attachments={attachments} rooms={data.rooms} room={room} chatNames={[data.name]} />
        </BaseCard>
      ) : null}
    </ObjectProvider>
  );
}
